import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  Shield, Key, Globe, Save, Loader2, CheckCircle, 
  AlertTriangle, Copy, ExternalLink, FileText, Plus,
  Trash2
} from 'lucide-react';
import { HelpButton } from '@/components/common/HelpButton';
import api from '@/services/api';

export const SSOPage = () => {
  const { accountId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ssoConfig, setSsoConfig] = useState(null);
  const [formData, setFormData] = useState({
    provider: 'saml',
    entity_id: '',
    sso_url: '',
    certificate: '',
    allowed_domains: [],
    is_enabled: false,
    auto_provision: true,
    default_role: 'viewer'
  });
  const [newDomain, setNewDomain] = useState('');
  
  const loadSSOConfig = useCallback(async () => {
    try {
      const response = await api.get(`/accounts/${accountId}/sso`);
      if (response.data) {
        setSsoConfig(response.data);
        setFormData({
          provider: response.data.provider || 'saml',
          entity_id: response.data.entity_id || '',
          sso_url: response.data.sso_url || '',
          certificate: response.data.certificate || '',
          allowed_domains: response.data.allowed_domains || [],
          is_enabled: response.data.is_enabled || false,
          auto_provision: response.data.auto_provision ?? true,
          default_role: response.data.default_role || 'viewer'
        });
      }
    } catch (error) {
      // SSO not configured yet - that's okay
      if (error.response?.status !== 404) {
        console.error('Failed to load SSO config:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [accountId]);
  
  useEffect(() => {
    loadSSOConfig();
  }, [loadSSOConfig]);
  
  const handleSave = async () => {
    if (!formData.entity_id || !formData.sso_url) {
      toast.error('Entity ID and SSO URL are required');
      return;
    }
    
    setSaving(true);
    try {
      if (ssoConfig) {
        await api.patch(`/accounts/${accountId}/sso`, formData);
      } else {
        await api.post(`/accounts/${accountId}/sso`, formData);
      }
      toast.success('SSO configuration saved');
      loadSSOConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save SSO configuration');
    } finally {
      setSaving(false);
    }
  };
  
  const handleAddDomain = () => {
    if (!newDomain) return;
    if (!newDomain.includes('.')) {
      toast.error('Please enter a valid domain (e.g., company.com)');
      return;
    }
    if (formData.allowed_domains.includes(newDomain)) {
      toast.error('Domain already added');
      return;
    }
    setFormData(prev => ({
      ...prev,
      allowed_domains: [...prev.allowed_domains, newDomain.toLowerCase()]
    }));
    setNewDomain('');
  };
  
  const handleRemoveDomain = (domain) => {
    setFormData(prev => ({
      ...prev,
      allowed_domains: prev.allowed_domains.filter(d => d !== domain)
    }));
  };
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };
  
  // Generate ACS URL and Entity ID for the IdP configuration
  const acsUrl = `${window.location.origin}/api/sso/callback/${accountId}`;
  const spEntityId = `${window.location.origin}/api/sso/metadata/${accountId}`;
  
  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-8" data-testid="sso-config-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Single Sign-On (SSO)</h1>
          <p className="text-slate-400 mt-1">Configure SAML-based enterprise authentication</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpButton 
            title="Single Sign-On (SSO)"
            content={`
              <p>Enable enterprise SSO to let team members sign in with your company's identity provider.</p>
              <p class="mt-2"><strong>Supported Providers:</strong></p>
              <ul>
                <li>Okta</li>
                <li>Azure Active Directory</li>
                <li>OneLogin</li>
                <li>Any SAML 2.0 compliant IdP</li>
              </ul>
              <p class="mt-2"><strong>Setup Steps:</strong></p>
              <ul>
                <li>1. Copy the Service Provider details (ACS URL and Entity ID)</li>
                <li>2. Add these to your Identity Provider's SAML configuration</li>
                <li>3. Copy your IdP's Entity ID, SSO URL, and Certificate</li>
                <li>4. Paste them in the configuration form and save</li>
              </ul>
              <p class="mt-2"><strong>Domain Restrictions:</strong> Add allowed email domains to restrict who can sign in via SSO.</p>
            `}
          />
          {ssoConfig?.is_enabled && (
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              <CheckCircle className="w-3 h-3 mr-1" />
              Active
            </Badge>
          )}
        </div>
      </div>
      
      {/* Enterprise Feature Notice */}
      <div className="p-4 bg-indigo-500/10 border border-indigo-500/30 rounded-lg flex items-start gap-3">
        <Shield className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-medium text-indigo-300">Enterprise Feature</h3>
          <p className="text-sm text-slate-400 mt-1">
            SSO is available on Enterprise plans. Configure SAML 2.0 to allow your team members to sign in with your company&apos;s identity provider (Okta, Azure AD, OneLogin, etc.)
          </p>
        </div>
      </div>
      
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Service Provider Info */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              Service Provider Details
            </CardTitle>
            <CardDescription>
              Use these values when configuring your Identity Provider
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs">ACS URL (Assertion Consumer Service)</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={acsUrl}
                  readOnly
                  className="bg-slate-900 border-slate-700 text-slate-300 text-sm font-mono"
                />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => copyToClipboard(acsUrl)}
                  className="border-slate-700"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs">SP Entity ID / Audience URI</Label>
              <div className="flex items-center gap-2">
                <Input
                  value={spEntityId}
                  readOnly
                  className="bg-slate-900 border-slate-700 text-slate-300 text-sm font-mono"
                />
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => copyToClipboard(spEntityId)}
                  className="border-slate-700"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div className="p-3 bg-slate-900/50 rounded-lg text-xs text-slate-400">
              <p className="font-medium text-slate-300 mb-2">Required SAML Attributes:</p>
              <ul className="space-y-1">
                <li>• <code className="text-indigo-400">email</code> - User&apos;s email address</li>
                <li>• <code className="text-indigo-400">firstName</code> - User&apos;s first name</li>
                <li>• <code className="text-indigo-400">lastName</code> - User&apos;s last name</li>
              </ul>
            </div>
          </CardContent>
        </Card>
        
        {/* IdP Configuration */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-white text-lg flex items-center gap-2">
              <Key className="w-5 h-5 text-indigo-400" />
              Identity Provider Configuration
            </CardTitle>
            <CardDescription>
              Enter the details from your identity provider
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-slate-300">IdP Entity ID</Label>
                <Input
                  placeholder="https://idp.example.com/entity"
                  value={formData.entity_id}
                  onChange={(e) => setFormData({ ...formData, entity_id: e.target.value })}
                  className="bg-slate-900 border-slate-700"
                  data-testid="sso-entity-id-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">SSO URL (Login URL)</Label>
                <Input
                  placeholder="https://idp.example.com/sso/saml"
                  value={formData.sso_url}
                  onChange={(e) => setFormData({ ...formData, sso_url: e.target.value })}
                  className="bg-slate-900 border-slate-700"
                  data-testid="sso-url-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">X.509 Certificate</Label>
              <textarea
                placeholder="-----BEGIN CERTIFICATE-----
MIICpDCCAYwCCQ...
-----END CERTIFICATE-----"
                value={formData.certificate}
                onChange={(e) => setFormData({ ...formData, certificate: e.target.value })}
                className="w-full h-32 px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-white placeholder:text-slate-500 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="sso-certificate-input"
              />
            </div>
            
            {/* Allowed Domains */}
            <div className="space-y-3">
              <Label className="text-slate-300">Allowed Email Domains</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="company.com"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddDomain()}
                  className="bg-slate-900 border-slate-700"
                  data-testid="sso-domain-input"
                />
                <Button 
                  onClick={handleAddDomain}
                  variant="outline"
                  className="border-slate-700"
                  data-testid="add-domain-btn"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.allowed_domains.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.allowed_domains.map((domain) => (
                    <Badge 
                      key={domain} 
                      variant="outline" 
                      className="border-slate-600 bg-slate-800 text-slate-300 pr-1"
                    >
                      @{domain}
                      <button
                        onClick={() => handleRemoveDomain(domain)}
                        className="ml-2 hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Only users with email addresses from these domains can use SSO
              </p>
            </div>
            
            {/* Options */}
            <div className="grid md:grid-cols-2 gap-6 pt-4 border-t border-slate-700">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-300">Enable SSO</Label>
                  <p className="text-xs text-slate-500 mt-1">Allow users to sign in via SSO</p>
                </div>
                <Switch
                  checked={formData.is_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_enabled: checked })}
                  data-testid="sso-enable-switch"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-slate-300">Auto-provision Users</Label>
                  <p className="text-xs text-slate-500 mt-1">Automatically create accounts for new SSO users</p>
                </div>
                <Switch
                  checked={formData.auto_provision}
                  onCheckedChange={(checked) => setFormData({ ...formData, auto_provision: checked })}
                  data-testid="sso-autoprovision-switch"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-300">Default Role for New Users</Label>
              <select
                value={formData.default_role}
                onChange={(e) => setFormData({ ...formData, default_role: e.target.value })}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                data-testid="sso-default-role-select"
              >
                <option value="viewer">Viewer</option>
                <option value="editor">Editor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            
            {/* Save Button */}
            <div className="flex items-center justify-between pt-4 border-t border-slate-700">
              <div className="text-sm text-slate-400">
                {ssoConfig && (
                  <span>Last updated: {new Date(ssoConfig.updated_at).toLocaleDateString()}</span>
                )}
              </div>
              <Button onClick={handleSave} disabled={saving} data-testid="save-sso-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Configuration
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Help Section */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader>
          <CardTitle className="text-white text-lg">Setup Guides</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { name: 'Okta', url: '#' },
              { name: 'Azure AD', url: '#' },
              { name: 'OneLogin', url: '#' },
            ].map((provider) => (
              <a
                key={provider.name}
                href={provider.url}
                className="flex items-center justify-between p-4 bg-slate-900/50 rounded-lg border border-slate-700 hover:border-indigo-500/50 transition-colors"
              >
                <span className="text-white font-medium">{provider.name}</span>
                <ExternalLink className="w-4 h-4 text-slate-400" />
              </a>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SSOPage;
